import { useEffect, useRef } from "react";
import { trustFeatures } from "@/mocks/product";

export default function TrustSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("animate-in");
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 md:py-16 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center mb-8 md:mb-12">
          <span
            data-animate
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-amber-100/80 text-amber-800 text-xs font-semibold tracking-widest uppercase mb-5 opacity-0 translate-y-4 [.animate-in_&]:opacity-100 [.animate-in_&]:translate-y-0 transition-all duration-600"
          >
            <i className="ri-verified-badge-line w-3 h-3 flex items-center justify-center" />
            Why NeoRoop
          </span>
          <h2
            data-animate
            className="font-heading text-3xl md:text-5xl font-bold text-accent-800 mt-3 opacity-0 translate-y-4 [.animate-in_&]:opacity-100 [.animate-in_&]:translate-y-0 transition-all duration-600 delay-100"
          >
            Trust in every thread
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {trustFeatures.map((feature, index) => (
            <div
              key={index}
              data-animate
              className="group relative bg-background-50 rounded-2xl p-7 md:p-8 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-md opacity-0 translate-y-6 [.animate-in_&]:opacity-100 [.animate-in_&]:translate-y-0"
              style={{ transitionDelay: `${200 + index * 100}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 mb-5 transition-all duration-300 group-hover:bg-amber-200 group-hover:scale-105">
                <i className={`${feature.icon} w-6 h-6 flex items-center justify-center text-xl`} />
              </div>
              <h3 className="font-heading text-lg md:text-xl font-semibold text-accent-800 mb-2.5">
                {feature.title}
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}